import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_MAINTENANCE_SETTINGS = {
  enabled: false,
  message: 'Estamos realizando mejoras en el sistema. Por favor, vuelve a intentarlo más tarde.',
  updated_at: null,
  updated_by: null
};

const normalizeSettings = (raw = {}) => ({
  enabled: Boolean(raw.enabled),
  message: String(raw.message || DEFAULT_MAINTENANCE_SETTINGS.message),
  updated_at: raw.updated_at || null,
  updated_by: raw.updated_by || null
});

const publishMaintenanceSettings = (settings) => {
  if (typeof window === 'undefined') return;
  const current = window.__MODELO_PRODUCCION_BOOTSTRAP__ || window.__APP_BOOTSTRAP__;
  if (!current || typeof current !== 'object') return;
  const nextBootstrap = { ...current, maintenance_mode: settings };
  window.__MODELO_PRODUCCION_BOOTSTRAP__ = nextBootstrap;
  window.__APP_BOOTSTRAP__ = nextBootstrap;
  window.dispatchEvent(new CustomEvent('modelo-bootstrap-ready', { detail: nextBootstrap }));
};

export const useAdminMaintenance = () => {
  const [settings, setSettings] = useState(DEFAULT_MAINTENANCE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const settingsRef = useRef(DEFAULT_MAINTENANCE_SETTINGS);

  const loadSettings = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/maintenance-settings', {
        headers: { Accept: 'application/json' },
        signal
      });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudo consultar el estado del sistema.');
      }
      const next = normalizeSettings(data.settings);
      if (!signal?.aborted) {
        settingsRef.current = next;
        setSettings(next);
      }
      return next;
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'No se pudo consultar el estado del sistema.');
      }
      throw err;
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const saveSettings = useCallback(async (values) => {
    const payload = {
      enabled: Boolean(values.enabled),
      message: String(values.message || '').trim()
    };
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/maintenance-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudo cambiar el estado del sistema.');
      }
      const next = normalizeSettings(data.settings);
      settingsRef.current = next;
      setSettings(next);
      publishMaintenanceSettings(next);
      return next;
    } catch (err) {
      setError(err?.message || 'No se pudo cambiar el estado del sistema.');
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSettings(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [loadSettings]);

  return { settings, loading, saving, error, loadSettings, saveSettings };
};
